import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Theme, Button, IconButton, Heading, TextField, TextArea } from '@radix-ui/themes';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import {
  calculateStandings,
  buildPointsConfig,
  buildQuestionPoints,
  clampVisualScore,
  markPoints,
  roundDefaultPoints,
  type ScoreRecord,
  type TeamRecord,
  type TeamStanding,
} from '../../utils/standings';
import { matchTeams } from './teamMatch';
import { useRoomSignals } from '../../utils/useRealtime';
import CheckInPanel from '../CheckIn/CheckInPanel';
import CheckInQr from '../CheckIn/CheckInQr';
import {
  detectCurrentRound,
  isRoundScored,
  roundProgress,
  roundQuestionMax,
  teamQuestionPct,
} from './roundFocus';
import './ScoringInterface.css';

// ────────────────────────────────────────────
// Types
// ────────────────────────────────────────────

interface Question {
  id: string;
  orderIndex: number;
  text: string;
  answer: string;
  // Own point value. null = the round default (pointsPerQuestion).
  points?: number | null;
}

interface Round {
  id: string;
  orderIndex: number;
  title: string;
  roundType: 'STANDARD' | 'VISUAL' | 'AUDIO';
  pointsPerQuestion?: number;  // 2 for STANDARD/AUDIO by default, 1 for VISUAL
  questions: Question[];
}

interface Quiz {
  id: string;
  name: string;
  rounds: Round[];
}

interface EventTeam {
  id: string;
  name: string;
  playerCount: number;
  doubledRound: number | null;
  leftEarly?: boolean;
  notes?: string | null;
  canonicalTeamId?: number | null;
  // Set when a team phone checked this team in (QR check-in).
  checkedInAt?: string | null;
}

interface LiveEvent {
  id: string;
  date: string;
  pin: string;
  status: string;
  checkInEnabled?: boolean;
  answerMode?: 'PAPER' | 'PHONE' | 'MIXED';
  venue: { id: number; name: string };
  quiz: Quiz;
  teams: EventTeam[];
  scores: ScoreRecord[];
}

// Score states for a question cell:
//   null    = unscored (em-dash)
//   'full'  = full credit (O) — awards pointsPerQuestion
//   'half'  = partial credit (½) — awards pointsPerQuestion / 2
//   'wrong' = no credit (X)
// Click cycle: null → full → half → wrong → null.
type ScoreState = null | 'full' | 'half' | 'wrong';

// ────────────────────────────────────────────
// Default config when no quiz is linked
// ────────────────────────────────────────────

const DEFAULT_ROUND_COUNT = 8;
const DEFAULT_QUESTION_COUNT = 8;

// ────────────────────────────────────────────
// Score Cell Component
// ────────────────────────────────────────────

function ScoreCell({
  state,
  onClick,
  ariaLabel,
}: {
  state: ScoreState;
  onClick: () => void;
  ariaLabel?: string;
}) {
  let className = 'si-score-btn ';
  let label: string;
  let stateDescription: string;
  if (state === null) {
    className += 'unscored';
    label = '\u2014'; // em dash
    stateDescription = 'unscored';
  } else if (state === 'full') {
    className += 'correct';
    label = 'O';
    stateDescription = 'correct';
  } else if (state === 'half') {
    className += 'partial';
    label = '\u00bd'; // \u00bd
    stateDescription = 'half credit';
  } else {
    className += 'incorrect';
    label = 'X';
    stateDescription = 'incorrect';
  }
  return (
    <button
      className={className}
      onClick={onClick}
      type="button"
      aria-label={ariaLabel ? `${ariaLabel} - ${stateDescription}` : stateDescription}
    >
      {label}
    </button>
  );
}

// ────────────────────────────────────────────
// Main Component
// ────────────────────────────────────────────

export default function ScoringInterface() {
  const { id } = useParams<{ id: string }>();

  const [event, setEvent] = useState<LiveEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [scores, setScores] = useState<ScoreRecord[]>([]);
  const [teams, setTeams] = useState<EventTeam[]>([]);

  // UI state
  // Two views of one screen (design 2d/1e). Sheets is default — built for
  // paper arriving out of order; Grid is the classic teams×questions matrix.
  // Persisted so an operator who prefers one keeps it across events.
  const [view, setView] = useState<'grid' | 'sheets'>(() => {
    try { return localStorage.getItem('scoring:view') === 'grid' ? 'grid' : 'sheets'; }
    catch { return 'sheets'; }
  });
  const selectView = useCallback((v: 'grid' | 'sheets') => {
    setView(v);
    try { localStorage.setItem('scoring:view', v); } catch { /* ignore */ }
  }, []);
  const [activeRound, setActiveRound] = useState<number | null>(null); // null = show all
  const [showStandings, setShowStandings] = useState(false);
  const [showAddTeam, setShowAddTeam] = useState(false);
  // Currently-open team-detail modal. Null = closed. Stores the team
  // id so we can re-resolve the team object on every render (state in
  // teams[] is the source of truth for the optimistic UI).
  const [detailTeamId, setDetailTeamId] = useState<string | null>(null);
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamCount, setNewTeamCount] = useState('');
  const [addingTeam, setAddingTeam] = useState(false);
  const [mobileSelectedTeam, setMobileSelectedTeam] = useState<string>('');
  const [showCheckins, setShowCheckins] = useState(false);

  // Per-cell debounce timers for visual-score writes, keyed by
  // `${teamId}:${roundIndex}`. The visual stepper fires onChange on every
  // click/keystroke; without debouncing, a fast burst of POSTs piled up
  // transactions on the same row and exhausted the small DB connection pool,
  // surfacing as "Score update failed" / "Failed to load event".
  const visualWriteTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  useEffect(
    () => () => {
      Object.values(visualWriteTimers.current).forEach(clearTimeout);
    },
    [],
  );

  // ── Fetch event data ──
  useEffect(() => {
    if (!id) return;
    setLoading(true);
    api
      .get(`/api/events/${id}`)
      .then((res) => {
        const ev: LiveEvent = res.data.data?.event ?? res.data.data ?? res.data;
        setEvent(ev);
        setTeams(ev.teams ?? []);
        setScores(ev.scores ?? []);
        // Open the round that the host most likely scores now. The screen
        // picks it one time, on load. Later score changes do not move it.
        const quizRounds = ev.quiz?.rounds ?? [];
        const roundNumbers =
          quizRounds.length > 0
            ? quizRounds.map((r) => r.orderIndex + 1)
            : Array.from({ length: DEFAULT_ROUND_COUNT }, (_, i) => i + 1);
        setActiveRound(detectCurrentRound(ev.scores ?? [], roundNumbers));
      })
      .catch((err) => {
        setError(err.response?.data?.error || 'Failed to load event');
      })
      .finally(() => setLoading(false));
  }, [id]);

  // ── Live phone check-ins ──
  // The server sends "teams-changed" when a phone checks in or the host
  // renames, merges, or rejects a team. Reload the team list. Reload scores
  // only when a team disappeared (merge or reject), so an in-flight score
  // click is not overwritten by an older server copy.
  const teamsRef = useRef<EventTeam[]>([]);
  teamsRef.current = teams;
  const reloadTeams = useCallback(() => {
    if (!id) return;
    api
      .get(`/api/events/${id}`)
      .then((res) => {
        const ev: LiveEvent = res.data.data?.event ?? res.data.data ?? res.data;
        const nextTeams = ev.teams ?? [];
        setEvent((prev) => (prev ? { ...prev, checkInEnabled: ev.checkInEnabled, answerMode: ev.answerMode } : prev));
        const nextIds = new Set(nextTeams.map((t) => t.id));
        if (teamsRef.current.some((t) => !nextIds.has(t.id))) setScores(ev.scores ?? []);
        setTeams(nextTeams);
      })
      .catch(() => {
        /* the next signal or a reload retries */
      });
  }, [id]);
  // The phone answer review saved marks: reload the score rows it wrote.
  const reloadScores = useCallback(() => {
    if (!id) return;
    api
      .get(`/api/events/${id}`)
      .then((res) => {
        const ev: LiveEvent = res.data.data?.event ?? res.data.data ?? res.data;
        setScores(ev.scores ?? []);
      })
      .catch(() => {
        /* the next signal or a reload retries */
      });
  }, [id]);
  useRoomSignals(
    id ? { event: 'join-host', eventId: id } : null,
    { 'teams-changed': reloadTeams, 'scores-changed': reloadScores },
    reloadTeams,
  );
  const phoneCheckins = useMemo(() => teams.filter((t) => t.checkedInAt).length, [teams]);

  // ── Build per-round points config ──
  // Map roundIndex (1-based) -> pointsPerQuestion. Standings consults this
  // so STANDARD rounds award 2 pts/q, VISUAL rounds 1, and any future
  // per-round override (e.g. a hard round at 3 pts) flows through.
  const pointsConfig = useMemo(
    () => buildPointsConfig(event?.quiz?.rounds ?? []),
    [event?.quiz?.rounds],
  );
  // questionId -> own value, for questions that override the round value.
  const questionPoints = useMemo(
    () => buildQuestionPoints(event?.quiz?.rounds ?? []),
    [event?.quiz?.rounds],
  );

  // ── Compute standings (memoized) ──
  const standings: TeamStanding[] = useMemo(() => {
    const teamRecords: TeamRecord[] = teams.map((t) => ({
      id: t.id,
      name: t.name,
      playerCount: t.playerCount,
      doubledRound: t.doubledRound,
      leftEarly: t.leftEarly,
    }));
    return calculateStandings(teamRecords, scores, pointsConfig, questionPoints);
  }, [teams, scores, pointsConfig, questionPoints]);

  const standingsMap = useMemo(
    () => new Map(standings.map((s) => [s.teamId, s])),
    [standings],
  );

  // Per-team question-correctness %. Excludes bonus and double-round
  // multipliers so the number is "how many questions did you actually
  // get right" rather than "how many points scored".
  // With a round tab open, the % covers that round only. With "All" open,
  // it covers the rounds that the team has a score for. Rounds that are
  // not played yet do not count. The old math divided by every round in
  // the quiz, so a perfect first round showed as 12.5% on an 8-round night.
  const teamPct = useCallback(
    (teamId: string): number | null =>
      teamQuestionPct(
        standingsMap.get(teamId),
        event?.quiz?.rounds ?? [],
        pointsConfig,
        scores,
        (roundIndex) => activeRound === null || roundIndex === activeRound,
      ),
    [standingsMap, event?.quiz?.rounds, pointsConfig, scores, activeRound],
  );

  // Headcount + average %. Excludes left-early teams from both — they
  // get baked into leftEarly filtering in calculateStandings, but we
  // also need to drop them from the player count.
  // The average counts only teams with a score in scope. A sheet that is
  // not graded yet does not pull the average down to zero.
  const stats = useMemo(() => {
    const active = teams.filter((t) => !t.leftEarly);
    const totalPlayers = active.reduce((sum, t) => sum + (t.playerCount ?? 0), 0);
    let totalPct = 0;
    let counted = 0;
    for (const t of active) {
      const pct = teamPct(t.id);
      if (pct == null) continue;
      totalPct += pct;
      counted++;
    }
    const avgPct = counted === 0 ? null : Math.round((totalPct / counted) * 10) / 10;
    return { activeTeams: active.length, totalPlayers, avgPct };
  }, [teams, teamPct]);

  // Sheets scored for the open round tab. Null when "All" is open.
  const activeRoundProgress = useMemo(
    () => (activeRound === null ? null : roundProgress(teams, scores, activeRound)),
    [teams, scores, activeRound],
  );

  // ── Build round descriptors from quiz or defaults ──
  // Visual rounds are first-class members of the round list — they
  // render inline at their natural position (e.g. R6 of 8), not in a
  // separate tab or trailing column.
  const allRounds: Round[] = useMemo(() => {
    const quizRounds = event?.quiz?.rounds ?? [];
    if (quizRounds.length > 0) {
      return [...quizRounds].sort((a, b) => a.orderIndex - b.orderIndex);
    }
    // No quiz linked: generate default rounds.
    // orderIndex is 0-based to match the real-data convention used by the
    // importer and roundRoutes (first round = orderIndex 0).
    return Array.from({ length: DEFAULT_ROUND_COUNT }, (_, i) => ({
      id: `default-round-${i + 1}`,
      orderIndex: i,
      title: `Round ${i + 1}`,
      roundType: 'STANDARD' as const,
      questions: Array.from({ length: DEFAULT_QUESTION_COUNT }, (_, q) => ({
        id: `default-r${i + 1}-q${q}`,
        orderIndex: q,
        text: `Question ${q + 1}`,
        answer: '',
      })),
    }));
  }, [event?.quiz?.rounds]);

  // ── Score helpers ──

  const getQuestionScore = useCallback(
    (teamId: string, roundIndex: number, questionId: string): ScoreState => {
      const rec = scores.find(
        (s) =>
          s.teamId === teamId &&
          s.roundIndex === roundIndex &&
          s.scoreType === 'QUESTION' &&
          s.questionId === questionId,
      );
      if (!rec) return null;
      if (rec.correct) return 'full';
      if (rec.partialCredit) return 'half';
      return 'wrong';
    },
    [scores],
  );

  const getBonusPoints = useCallback(
    (teamId: string, roundIndex: number): number => {
      const rec = scores.find(
        (s) =>
          s.teamId === teamId &&
          s.roundIndex === roundIndex &&
          s.scoreType === 'BONUS',
      );
      if (!rec) return 0;
      // Use bonusPoints if present, fall back to title+artist
      if (rec.bonusPoints != null) return rec.bonusPoints;
      return (rec.bonusTitleCorrect ? 1 : 0) + (rec.bonusArtistCorrect ? 1 : 0);
    },
    [scores],
  );

  const getVisualScore = useCallback(
    (teamId: string, roundIndex: number): number => {
      const rec = scores.find(
        (s) =>
          s.teamId === teamId &&
          s.roundIndex === roundIndex &&
          s.scoreType === 'VISUAL',
      );
      return rec?.visualScore ?? 0;
    },
    [scores],
  );

  // ── Score mutation: question toggle ──
  // Four-state cycle: null → full → half → wrong → null. Half-credit awards
  // half of the question value (rounded down) in the standings calculation.
  const toggleQuestionScore = (
    teamId: string,
    roundIndex: number,
    questionId: string,
  ) => {
    const current = getQuestionScore(teamId, roundIndex, questionId);
    let next: ScoreState;
    if (current === null) next = 'full';
    else if (current === 'full') next = 'half';
    else if (current === 'half') next = 'wrong';
    else next = null;

    // Optimistic update
    setScores((prev) => {
      const filtered = prev.filter(
        (s) =>
          !(
            s.teamId === teamId &&
            s.roundIndex === roundIndex &&
            s.scoreType === 'QUESTION' &&
            s.questionId === questionId
          ),
      );
      if (next === null) return filtered;
      return [
        ...filtered,
        {
          teamId,
          questionId,
          roundIndex,
          scoreType: 'QUESTION' as const,
          correct: next === 'full',
          partialCredit: next === 'half',
          bonusTitleCorrect: false,
          bonusArtistCorrect: false,
          bonusPoints: null,
          visualScore: null,
        },
      ];
    });

    // Fire API
    if (next === null) {
      api
        .delete('/api/scores', {
          data: { teamId, questionId, roundIndex, scoreType: 'QUESTION' },
        })
        .catch(() => toast.error('Score update failed'));
    } else {
      api
        .post('/api/scores', {
          teamId,
          questionId,
          roundIndex,
          scoreType: 'QUESTION',
          correct: next === 'full',
          partialCredit: next === 'half',
        })
        .catch(() => toast.error('Score update failed'));
    }
  };

  // ── Score mutation: set a question to a SPECIFIC state ──
  // The grid cycles (toggleQuestionScore); the Sheets view needs to set an
  // exact state from its ✓/✗ buttons. Same optimistic + API path.
  const setQuestionScore = (
    teamId: string,
    roundIndex: number,
    questionId: string,
    next: ScoreState,
  ) => {
    setScores((prev) => {
      const filtered = prev.filter(
        (s) =>
          !(
            s.teamId === teamId &&
            s.roundIndex === roundIndex &&
            s.scoreType === 'QUESTION' &&
            s.questionId === questionId
          ),
      );
      if (next === null) return filtered;
      return [
        ...filtered,
        {
          teamId,
          questionId,
          roundIndex,
          scoreType: 'QUESTION' as const,
          correct: next === 'full',
          partialCredit: next === 'half',
          bonusTitleCorrect: false,
          bonusArtistCorrect: false,
          bonusPoints: null,
          visualScore: null,
        },
      ];
    });

    if (next === null) {
      api
        .delete('/api/scores', {
          data: { teamId, questionId, roundIndex, scoreType: 'QUESTION' },
        })
        .catch(() => toast.error('Score update failed'));
    } else {
      api
        .post('/api/scores', {
          teamId,
          questionId,
          roundIndex,
          scoreType: 'QUESTION',
          correct: next === 'full',
          partialCredit: next === 'half',
        })
        .catch(() => toast.error('Score update failed'));
    }
  };

  // ── Score mutation: bonus (single integer) ──
  const setBonusValue = (
    teamId: string,
    roundIndex: number,
    value: number,
  ) => {
    const clamped = Math.max(0, value);

    // Optimistic update
    setScores((prev) => {
      const filtered = prev.filter(
        (s) =>
          !(
            s.teamId === teamId &&
            s.roundIndex === roundIndex &&
            s.scoreType === 'BONUS'
          ),
      );
      return [
        ...filtered,
        {
          teamId,
          questionId: null,
          roundIndex,
          scoreType: 'BONUS' as const,
          correct: false,
          bonusTitleCorrect: false,
          bonusArtistCorrect: false,
          bonusPoints: clamped,
          visualScore: null,
        },
      ];
    });

    api
      .post('/api/scores', {
        teamId,
        roundIndex,
        scoreType: 'BONUS',
        bonusPoints: clamped,
      })
      .catch(() => toast.error('Score update failed'));
  };

  // ── Score mutation: visual score ──
  // roundIndex is the visual round's natural position (e.g. 6 for R6) —
  // no more sentinel-0 convention.
  const setVisualScoreValue = (
    teamId: string,
    roundIndex: number,
    value: number,
  ) => {
    const items = (event?.quiz?.rounds ?? []).find(
      (r) => r.orderIndex + 1 === roundIndex,
    )?.questions.length;
    const clamped = clampVisualScore(value, items);

    // Optimistic update
    setScores((prev) => {
      const filtered = prev.filter(
        (s) =>
          !(
            s.teamId === teamId &&
            s.roundIndex === roundIndex &&
            s.scoreType === 'VISUAL'
          ),
      );
      return [
        ...filtered,
        {
          teamId,
          questionId: null,
          roundIndex,
          scoreType: 'VISUAL' as const,
          correct: false,
          bonusTitleCorrect: false,
          bonusArtistCorrect: false,
          bonusPoints: null,
          visualScore: clamped,
        },
      ];
    });

    // Debounce the persist: collapse a burst of rapid clicks/keystrokes on
    // this cell into a single POST carrying the latest value. The optimistic
    // update above keeps the UI responsive in the meantime.
    const key = `${teamId}:${roundIndex}`;
    if (visualWriteTimers.current[key]) {
      clearTimeout(visualWriteTimers.current[key]);
    }
    visualWriteTimers.current[key] = setTimeout(() => {
      delete visualWriteTimers.current[key];
      api
        .post('/api/scores', {
          teamId,
          roundIndex,
          scoreType: 'VISUAL',
          visualScore: clamped,
        })
        .catch(() => toast.error('Score update failed'));
    }, 350);
  };

  // ── Double round toggle ──
  const toggleDouble = (teamId: string, roundIndex: number) => {
    const team = teams.find((t) => t.id === teamId);
    if (!team) return;

    const isCurrentlyDoubled = team.doubledRound === roundIndex;
    const newDoubled = isCurrentlyDoubled ? null : roundIndex;

    // Optimistic update
    setTeams((prev) =>
      prev.map((t) =>
        t.id === teamId ? { ...t, doubledRound: newDoubled } : t,
      ),
    );

    api
      .put(`/api/teams/${teamId}/double`, {
        roundIndex: newDoubled,
      })
      .catch(() => toast.error('Score update failed'));
  };

  // ── Rename team — optimistic, rolls back on server error ──
  const renameTeam = (teamId: string, nextName: string) => {
    const trimmed = nextName.trim();
    const team = teams.find((t) => t.id === teamId);
    if (!team) return;
    if (!trimmed || trimmed === team.name) return; // no-op
    const prevName = team.name;
    setTeams((prev) =>
      prev.map((t) => (t.id === teamId ? { ...t, name: trimmed } : t)),
    );
    api
      .put(`/api/teams/${teamId}`, { name: trimmed })
      .catch(() => {
        toast.error('Failed to rename team');
        setTeams((prev) =>
          prev.map((t) => (t.id === teamId ? { ...t, name: prevName } : t)),
        );
      });
  };

  // ── Partial update: persists any subset of {playerCount, notes,
  // leftEarly, name}. Used by the team-detail modal which mutates
  // several fields together. Optimistic + rolls back on server error.
  const updateTeam = (teamId: string, patch: Partial<EventTeam>) => {
    const team = teams.find((t) => t.id === teamId);
    if (!team) return;
    const prev = { ...team };
    setTeams((all) => all.map((t) => (t.id === teamId ? { ...t, ...patch } : t)));
    api
      .put(`/api/teams/${teamId}`, patch)
      .catch(() => {
        toast.error('Failed to save team');
        setTeams((all) => all.map((t) => (t.id === teamId ? prev : t)));
      });
  };

  // ── Toggle "left early" — hides team from standings and audience board ──
  const toggleLeftEarly = (teamId: string) => {
    const team = teams.find((t) => t.id === teamId);
    if (!team) return;
    const nextLeft = !team.leftEarly;

    // Optimistic update
    setTeams((prev) =>
      prev.map((t) => (t.id === teamId ? { ...t, leftEarly: nextLeft } : t)),
    );

    api
      .put(`/api/teams/${teamId}`, { leftEarly: nextLeft })
      .catch(() => {
        toast.error('Failed to update team status');
        // Rollback
        setTeams((prev) =>
          prev.map((t) => (t.id === teamId ? { ...t, leftEarly: !nextLeft } : t)),
        );
      });
  };

  // ── Add team ──
  const handleAddTeam = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTeamName.trim() || !id) return;
    setAddingTeam(true);
    try {
      const res = await api.post(`/api/teams/event/${id}`, {
        name: newTeamName.trim(),
        playerCount: newTeamCount ? parseInt(newTeamCount, 10) : undefined,
      });
      const newTeam: EventTeam = res.data.data?.team ?? res.data.data ?? res.data;
      setTeams((prev) => [...prev, newTeam]);
      setNewTeamName('');
      setNewTeamCount('');
      setShowAddTeam(false);
    } catch {
      toast.error('Failed to add team');
    } finally {
      setAddingTeam(false);
    }
  };

  // ── Determine visible rounds ──
  // Match by orderIndex+1 (the 1-based round number), not array position.
  // orderIndex is 0-based in the data; score.roundIndex is 1-based.
  const visibleRounds =
    activeRound === null
      ? allRounds
      : allRounds.filter((r) => r.orderIndex + 1 === activeRound);

  // True when one round tab is open. The night total and rank then show
  // as secondary, and the % describes the open round only.
  const nightSecondary = activeRound !== null;

  // ── Render ──

  if (loading) {
    return (
      <Theme hasBackground={false}>
        <div className="si-loading">Loading event...</div>
      </Theme>
    );
  }

  if (error || !event) {
    return (
      <Theme hasBackground={false}>
        <div className="si-error">{error || 'Event not found'}</div>
      </Theme>
    );
  }

  const eventDate = event.date
    ? new Date(event.date).toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : '';

  return (
    <Theme hasBackground={false}>
    <div className="si-page">
      {/* ── Top Bar ── */}
      <div className="si-topbar">
        <Link to="/live-events" className="si-back-link">
          &larr; Events
        </Link>
        <div className="si-event-info">
          <div className="si-event-name">{event.quiz?.name ?? 'Untitled'}</div>
          <div className="si-event-meta">
            {event.venue?.name}
            {eventDate && ` \u00b7 ${eventDate}`}
          </div>
        </div>
        <div className="si-view-toggle" role="tablist" aria-label="Scoring view">
          <button
            type="button"
            role="tab"
            aria-selected={view === 'grid'}
            className={`si-view-tab ${view === 'grid' ? 'active' : ''}`}
            onClick={() => selectView('grid')}
          >
            Grid
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={view === 'sheets'}
            className={`si-view-tab ${view === 'sheets' ? 'active' : ''}`}
            onClick={() => selectView('sheets')}
          >
            Sheets
          </button>
        </div>
        <span className="si-team-count">
          <strong>{teams.length}</strong> teams
        </span>
        <Button
          size="1"
          variant="outline"
          color="gray"
          onClick={() => setShowAddTeam(true)}
        >
          + Team
        </Button>
        {event.answerMode && event.answerMode !== 'PAPER' && (
          <Button asChild size="1" variant="outline" color="gray">
            <Link to={`/events/${id}/answers`} title="Review and mark phone answers">
              Phone answers
            </Link>
          </Button>
        )}
        {(event.checkInEnabled || phoneCheckins > 0) && (
          <Button
            size="1"
            variant="outline"
            color="gray"
            onClick={() => setShowCheckins(true)}
            title="Teams that checked in with the QR code"
          >
            Check-ins ({phoneCheckins})
          </Button>
        )}
        <Button asChild size="1" variant="outline">
          <Link
            to={`/events/${id}/host`}
            title="Open the host control panel for this event"
          >
            Host
          </Link>
        </Button>
        <Button
          size="1"
          variant="outline"
          onClick={() => setShowStandings(true)}
        >
          Standings
        </Button>
      </div>

      {view === 'grid' && (<>
      {/* ── Stats strip — headcount + average correctness % ── */}
      <div className="si-stats-strip">
        <div className="si-stat">
          <span className="si-stat-value">{stats.totalPlayers}</span>
          <span className="si-stat-label">people here</span>
        </div>
        <div className="si-stat">
          <span className="si-stat-value">{stats.activeTeams}</span>
          <span className="si-stat-label">active teams</span>
        </div>
        <div className="si-stat">
          <span className="si-stat-value">
            {activeRoundProgress
              ? `${activeRoundProgress.scored}/${activeRoundProgress.total}`
              : '—'}
          </span>
          <span className="si-stat-label">
            {activeRound === null ? 'pick a round' : `R${activeRound} sheets scored`}
          </span>
        </div>
        <div
          className="si-stat"
          title={
            activeRound === null
              ? 'Average question-correct % over the rounds scored so far'
              : `Average question-correct % in round ${activeRound}, for scored sheets only`
          }
        >
          <span className="si-stat-value">
            {stats.avgPct == null ? '—' : `${stats.avgPct}%`}
          </span>
          <span className="si-stat-label">
            {activeRound === null ? 'avg correct' : `R${activeRound} avg correct`}
          </span>
        </div>
      </div>

      {/* ── Round Tabs ── */}
      <div className="si-round-tabs">
        <button
          className={`si-round-tab-all ${activeRound === null ? 'active' : ''}`}
          onClick={() => setActiveRound(null)}
        >
          All
        </button>
        {allRounds.map((round) => {
          // orderIndex is 0-based in the data; the user-facing round number
          // (and the score.roundIndex written to the DB) is 1-based.
          const roundNum = round.orderIndex + 1;
          const tabLabel =
            round.roundType === 'VISUAL' ? `R${roundNum} Vis` : `R${roundNum}`;
          return (
            <button
              key={round.id}
              className={`si-round-tab ${activeRound === roundNum ? 'active' : ''}`}
              onClick={() => setActiveRound(roundNum)}
              title={round.title || `Round ${roundNum}`}
            >
              {tabLabel}
            </button>
          );
        })}
      </div>

      {/* ── Empty State ── */}
      {teams.length === 0 && (
        <div className="si-empty-state">
          <p>No teams yet. Add teams to start scoring.</p>
          <Button onClick={() => setShowAddTeam(true)}>
            Add Team
          </Button>
        </div>
      )}

      {/* ── Scoring Table (desktop) ── */}
      {teams.length > 0 && (
        <div className="si-table-wrap si-desktop-only">
          <table className="si-table">
            <thead>
              {/* Group header row */}
              <tr>
                <th className="si-th-team" rowSpan={2}>
                  Team
                </th>
                {visibleRounds.map((round, i) => {
                  // orderIndex is 0-based; user-facing round number is 1-based.
                  const roundNum = round.orderIndex + 1;
                  const isVisual = round.roundType === 'VISUAL';
                  // VISUAL rounds collapse to one input \u2014 pts + 2x + tot = 3
                  // cols. STANDARD/AUDIO rounds use questions + bonus + 2x +
                  // tot = qCount + 3 cols.
                  const colSpan = isVisual
                    ? 3
                    : [...round.questions].length + 3;
                  const headerLabel = isVisual
                    ? `R${roundNum} \u2014 ${round.title || 'Visual'}`
                    : `R${roundNum}${round.title ? ` \u2014 ${round.title}` : ''}`;
                  return [
                    i > 0 && (
                      <th key={`sep-${round.id}`} className="si-th-separator" rowSpan={2} />
                    ),
                    <th
                      key={`rh-${round.id}`}
                      className="si-th-round-group"
                      colSpan={colSpan}
                      title={round.title || `Round ${roundNum}`}
                    >
                      {headerLabel}
                    </th>,
                  ];
                })}
                {visibleRounds.length > 0 && (
                  <th className="si-th-separator" rowSpan={2} />
                )}
                {/* With one round open, the night total stays visible but
                    muted. The round Tot column is the number to check. */}
                <th
                  className={`si-th-round-group ${nightSecondary ? 'si-night-secondary' : ''}`}
                  colSpan={2}
                  title={nightSecondary ? 'Whole-night total and rank' : undefined}
                >
                  {nightSecondary ? 'Night' : 'Total'}
                </th>
              </tr>
              {/* Sub-header row */}
              <tr>
                {visibleRounds.map((round) => {
                  const cols = [];
                  if (round.roundType === 'VISUAL') {
                    cols.push(<th key={`pts-${round.id}`}>Pts</th>);
                  } else {
                    const questions = [...round.questions].sort(
                      (a, b) => a.orderIndex - b.orderIndex,
                    );
                    const roundPts = roundDefaultPoints(round);
                    for (let q = 0; q < questions.length; q++) {
                      // Show the value when a question does not use the
                      // round value, so the host sees why a total jumps.
                      const own = questions[q]!.points;
                      const differs = own != null && own !== roundPts;
                      cols.push(
                        <th
                          key={`q-${round.id}-${q}`}
                          title={differs ? `Worth ${own} ${own === 1 ? 'point' : 'points'}` : undefined}
                        >
                          Q{q + 1}
                          {differs && <span className="si-th-pts">{own}pt</span>}
                        </th>,
                      );
                    }
                    cols.push(<th key={`bn-${round.id}`}>Bon</th>);
                  }
                  cols.push(<th key={`db-${round.id}`}>2x</th>);
                  cols.push(<th key={`rt-${round.id}`}>Tot</th>);
                  return cols;
                })}
                <th className={nightSecondary ? 'si-night-secondary' : ''}>Sum</th>
                <th className={nightSecondary ? 'si-night-secondary' : ''}>#</th>
              </tr>
            </thead>
            <tbody>
              {teams.map((team) => {
                const standing = standingsMap.get(team.id);
                const grandTotal = standing?.grandTotal ?? 0;
                const rank = standing?.rank ?? 0;

                return (
                  <tr key={team.id} className={team.leftEarly ? 'si-team-left' : ''}>
                    <td className="si-td-team">
                      <TeamNameCell
                        name={team.name}
                        onCommit={(next) => renameTeam(team.id, next)}
                      />
                      {team.leftEarly && (
                        <span className="si-team-left-badge" title="Excluded from standings">
                          left
                        </span>
                      )}
                      {team.doubledRound ? (
                        <span className="si-double-badge used" title={`Doubled R${team.doubledRound}`}>
                          2x R{team.doubledRound}
                        </span>
                      ) : (
                        <span className="si-double-badge unused" title="Double not yet used">
                          2x ?
                        </span>
                      )}
                      <button
                        className="si-team-detail-btn"
                        onClick={() => setDetailTeamId(team.id)}
                        type="button"
                        title="Show team details — edit player count, notes, and view round breakdown"
                      >
                        Details
                      </button>
                      <button
                        className={`si-team-left-toggle ${team.leftEarly ? 'active' : ''}`}
                        onClick={() => toggleLeftEarly(team.id)}
                        type="button"
                        title={team.leftEarly ? 'Return team to standings' : 'Mark team as left — hides from standings'}
                      >
                        {team.leftEarly ? 'Return' : 'Mark Left'}
                      </button>
                    </td>
                    {visibleRounds.map((round, i) => {
                      // score.roundIndex is 1-based per schema convention
                      // (orderIndex is 0-based). Visual rounds also store
                      // at their natural orderIndex+1 — no sentinel.
                      const roundIndex = round.orderIndex + 1;
                      const isVisual = round.roundType === 'VISUAL';
                      const isDoubled = team.doubledRound === roundIndex;
                      const roundStanding = standing?.roundScores.find(
                        (rs) => rs.roundIndex === roundIndex,
                      );
                      const roundTotal = roundStanding?.total ?? 0;

                      const cells = [];

                      // Separator
                      if (i > 0) {
                        cells.push(
                          <td key={`sep-${team.id}-${round.id}`} className="si-td-separator" />,
                        );
                      }

                      if (isVisual) {
                        // Visual round: single number input (no per-question
                        // cells, no bonus).
                        cells.push(
                          <td key={`pts-${team.id}-${round.id}`}>
                            <input
                              type="number"
                              className="si-visual-input"
                              min={0}
                              max={round.questions.length || undefined}
                              value={getVisualScore(team.id, roundIndex)}
                              onChange={(e) =>
                                setVisualScoreValue(
                                  team.id,
                                  roundIndex,
                                  parseInt(e.target.value, 10) || 0,
                                )
                              }
                              aria-label={`Visual score for ${team.name} Round ${roundIndex}`}
                            />
                          </td>,
                        );
                      } else {
                        const questions = [...round.questions].sort(
                          (a, b) => a.orderIndex - b.orderIndex,
                        );
                        const bonusVal = getBonusPoints(team.id, roundIndex);

                        // Question cells
                        for (let q = 0; q < questions.length; q++) {
                          const question = questions[q]!;
                          const state = getQuestionScore(
                            team.id,
                            roundIndex,
                            question.id,
                          );
                          cells.push(
                            <td key={`q-${team.id}-${round.id}-${q}`}>
                              <ScoreCell
                                state={state}
                                onClick={() =>
                                  toggleQuestionScore(
                                    team.id,
                                    roundIndex,
                                    question.id,
                                  )
                                }
                                ariaLabel={`${team.name} Round ${roundIndex} Q${q + 1}`}
                              />
                            </td>,
                          );
                        }

                        // Bonus cell (single number input)
                        cells.push(
                          <td key={`bn-${team.id}-${round.id}`}>
                            <input
                              type="number"
                              className="si-bonus-input"
                              min={0}
                              value={bonusVal}
                              onChange={(e) =>
                                setBonusValue(
                                  team.id,
                                  roundIndex,
                                  parseInt(e.target.value, 10) || 0,
                                )
                              }
                              aria-label={`Bonus for ${team.name} Round ${roundIndex}`}
                            />
                          </td>,
                        );
                      }

                      // Double cell (shared between visual and standard)
                      cells.push(
                        <td key={`db-${team.id}-${round.id}`}>
                          <div className="si-double-cell">
                            <input
                              type="checkbox"
                              className="si-double-checkbox"
                              checked={isDoubled}
                              onChange={() =>
                                toggleDouble(team.id, roundIndex)
                              }
                              aria-label={`Double round ${roundIndex} for ${team.name}`}
                            />
                          </div>
                        </td>,
                      );

                      // Round total (shared). With one round open, the
                      // round % sits here, next to the number it explains.
                      const roundPct = nightSecondary ? teamPct(team.id) : null;
                      cells.push(
                        <td key={`rt-${team.id}-${round.id}`}>
                          <span
                            className={`si-round-total ${isDoubled ? 'doubled' : ''}`}
                          >
                            {roundTotal}
                          </span>
                          {nightSecondary && (
                            <span
                              className="si-team-pct"
                              title={`Question-correct % in round ${roundIndex} (bonus and double not included)`}
                            >
                              {roundPct == null ? '' : `${roundPct}%`}
                            </span>
                          )}
                        </td>,
                      );

                      return cells;
                    })}

                    {/* Separator before totals */}
                    {visibleRounds.length > 0 && (
                      <td className="si-td-separator" />
                    )}

                    {/* Grand total */}
                    <td className={nightSecondary ? 'si-night-secondary' : ''}>
                      <span className="si-grand-total">{grandTotal}</span>
                      {!nightSecondary && (() => {
                        const pct = teamPct(team.id);
                        return pct == null ? null : (
                          <span className="si-team-pct" title="Question-correct % over the rounds scored so far (bonus and double not included)">
                            {pct}%
                          </span>
                        );
                      })()}
                    </td>

                    {/* Rank */}
                    <td className={nightSecondary ? 'si-night-secondary' : ''}>
                      <span
                        className={`si-rank ${
                          rank === 1
                            ? 'si-rank-1'
                            : rank === 2
                              ? 'si-rank-2'
                              : rank === 3
                                ? 'si-rank-3'
                                : ''
                        }`}
                      >
                        {rank || ''}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Mobile Scoring View ── */}
      {teams.length > 0 && (
        <div className="si-mobile-only">
          <div className="si-mobile-team-picker">
            <select
              className="si-mobile-select"
              value={mobileSelectedTeam}
              onChange={(e) => setMobileSelectedTeam(e.target.value)}
            >
              <option value="">Select a team...</option>
              {teams.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>

          {(() => {
            const team = teams.find((t) => t.id === mobileSelectedTeam);
            if (!team) return null;
            const standing = standingsMap.get(team.id);
            const grandTotal = standing?.grandTotal ?? 0;
            const rank = standing?.rank ?? 0;

            return (
              <div className="si-mobile-cards">
                {/* Grand total banner */}
                <div
                  className={`si-mobile-total-banner ${nightSecondary ? 'si-night-secondary' : ''}`}
                >
                  <span className="si-mobile-total-label">
                    {nightSecondary ? 'Night' : 'Total'}
                  </span>
                  <span className="si-mobile-total-value">{grandTotal}</span>
                  {!nightSecondary && (() => {
                    const pct = teamPct(team.id);
                    return pct == null ? null : (
                      <span className="si-team-pct">{pct}%</span>
                    );
                  })()}
                  {rank > 0 && (
                    <span
                      className={`si-mobile-rank ${
                        rank === 1
                          ? 'si-rank-1'
                          : rank === 2
                            ? 'si-rank-2'
                            : rank === 3
                              ? 'si-rank-3'
                              : ''
                      }`}
                    >
                      #{rank}
                    </span>
                  )}
                </div>

                {visibleRounds.map((round) => {
                  // orderIndex is 0-based; score.roundIndex is 1-based.
                  const roundIndex = round.orderIndex + 1;
                  const isVisual = round.roundType === 'VISUAL';
                  const isDoubled = team.doubledRound === roundIndex;
                  const roundStanding = standing?.roundScores.find(
                    (rs) => rs.roundIndex === roundIndex,
                  );
                  const roundTotal = roundStanding?.total ?? 0;

                  return (
                    <div key={round.id} className="si-mobile-round-card">
                      <div className="si-mobile-round-header">
                        <span className="si-mobile-round-title">
                          R{roundIndex}
                          {round.title ? ` \u2014 ${round.title}` : isVisual ? ' \u2014 Visual' : ''}
                        </span>
                        <span
                          className={`si-mobile-round-total ${isDoubled ? 'doubled' : ''}`}
                        >
                          {roundTotal}
                        </span>
                      </div>

                      {isVisual ? (
                        <div className="si-mobile-visual-row">
                          <span className="si-mobile-q-label">Score</span>
                          <input
                            type="number"
                            className="si-visual-input si-mobile-visual-input"
                            min={0}
                            max={round.questions.length || undefined}
                            value={getVisualScore(team.id, roundIndex)}
                            onChange={(e) =>
                              setVisualScoreValue(
                                team.id,
                                roundIndex,
                                parseInt(e.target.value, 10) || 0,
                              )
                            }
                            aria-label={`Visual score for ${team.name} Round ${roundIndex}`}
                          />
                        </div>
                      ) : (
                        <>
                          {/* Questions */}
                          <div className="si-mobile-questions">
                            {[...round.questions]
                              .sort((a, b) => a.orderIndex - b.orderIndex)
                              .map((question, q) => {
                                const state = getQuestionScore(
                                  team.id,
                                  roundIndex,
                                  question.id,
                                );
                                const mobileClass =
                                  state === null   ? 'unscored'  :
                                  state === 'full' ? 'correct'   :
                                  state === 'half' ? 'partial'   :
                                                     'incorrect';
                                const mobileLabel =
                                  state === null   ? '\u2014' :
                                  state === 'full' ? 'O' :
                                  state === 'half' ? '\u00bd' :
                                                     'X';
                                return (
                                  <div key={question.id} className="si-mobile-q-row">
                                    <span className="si-mobile-q-label">Q{q + 1}</span>
                                    <button
                                      className={`si-mobile-score-btn ${mobileClass}`}
                                      onClick={() =>
                                        toggleQuestionScore(
                                          team.id,
                                          roundIndex,
                                          question.id,
                                        )
                                      }
                                      type="button"
                                      aria-label={`${team.name} Round ${roundIndex} Q${q + 1}`}
                                    >
                                      {mobileLabel}
                                    </button>
                                  </div>
                                );
                              })}
                          </div>

                          {/* Bonus (single number input) */}
                          <div className="si-mobile-bonus-row">
                            <span className="si-mobile-q-label">Bonus</span>
                            <input
                              type="number"
                              className="si-bonus-input si-mobile-bonus-input"
                              min={0}
                              value={getBonusPoints(team.id, roundIndex)}
                              onChange={(e) =>
                                setBonusValue(
                                  team.id,
                                  roundIndex,
                                  parseInt(e.target.value, 10) || 0,
                                )
                              }
                              aria-label={`Bonus for ${team.name} Round ${roundIndex}`}
                            />
                          </div>
                        </>
                      )}

                      {/* Double (shared between visual and standard) */}
                      <div className="si-mobile-double-row">
                        <span className="si-mobile-q-label">Double</span>
                        <label className="si-mobile-double-label">
                          <input
                            type="checkbox"
                            className="si-double-checkbox"
                            checked={isDoubled}
                            onChange={() =>
                              toggleDouble(team.id, roundIndex)
                            }
                          />
                          2x
                        </label>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      )}
      </>)}

      {/* ── Sheets view (design 2d) — one team's sheet at a time, built
          for paper arriving out of order. Reuses the same scores/handlers
          as the grid so writes are one shared source of truth. ── */}
      {view === 'sheets' && teams.length > 0 && (
        <SheetsView
          teams={teams}
          rounds={allRounds}
          scores={scores}
          standings={standings}
          getQuestionScore={getQuestionScore}
          setQuestionScore={setQuestionScore}
          getBonusPoints={getBonusPoints}
          setBonusValue={setBonusValue}
          toggleDouble={toggleDouble}
          pointsConfig={pointsConfig}
          initialRound={activeRound ?? undefined}
          onRoundChange={setActiveRound}
        />
      )}
      {view === 'sheets' && teams.length === 0 && (
        <div className="si-empty-state">
          <p>No teams yet. Add teams to start scoring.</p>
          <Button onClick={() => setShowAddTeam(true)}>Add Team</Button>
        </div>
      )}

      {/* ── Team Detail Modal ── */}
      {detailTeamId && (() => {
        const team = teams.find((t) => t.id === detailTeamId);
        if (!team) return null;
        const standing = standingsMap.get(team.id);
        return (
          <div className="si-overlay" onClick={() => setDetailTeamId(null)}>
            <div className="si-modal" onClick={(e) => e.stopPropagation()}>
              <div className="si-modal-header">
                <Heading as="h2" size="3">{team.name}</Heading>
                <IconButton
                  size="1"
                  variant="ghost"
                  color="gray"
                  aria-label="Close"
                  onClick={() => setDetailTeamId(null)}
                >
                  &times;
                </IconButton>
              </div>
              <div className="si-modal-body">
                <div className="si-detail-grid">
                  <label className="si-detail-field">
                    <span className="si-detail-label">Players</span>
                    <TextField.Root
                      size="2"
                      type="number"
                      min={1}
                      value={team.playerCount}
                      onChange={(e) => {
                        const n = parseInt(e.target.value, 10);
                        if (!Number.isNaN(n) && n >= 1) {
                          updateTeam(team.id, { playerCount: n });
                        }
                      }}
                    />
                  </label>
                  <label className="si-detail-field">
                    <span className="si-detail-label">Doubled Round</span>
                    <span className="si-detail-static">
                      {team.doubledRound ? `Round ${team.doubledRound}` : '—'}
                    </span>
                  </label>
                  <label className="si-detail-field">
                    <span className="si-detail-label">Status</span>
                    <span className="si-detail-static">
                      {team.leftEarly ? 'Left early — excluded from standings' : 'Active'}
                    </span>
                  </label>
                  {standing && (
                    <label className="si-detail-field">
                      <span className="si-detail-label">Total / Rank</span>
                      <span className="si-detail-static">
                        {standing.grandTotal} pts {standing.rank ? `· #${standing.rank}` : ''}
                      </span>
                    </label>
                  )}
                  <label className="si-detail-field si-detail-field-wide">
                    <span className="si-detail-label">Host notes</span>
                    <TextArea
                      size="2"
                      resize="vertical"
                      value={team.notes ?? ''}
                      onChange={(e) => updateTeam(team.id, { notes: e.target.value })}
                      placeholder="Anything to remember about this team — birthday, regulars, etc."
                      rows={4}
                    />
                  </label>
                </div>

                {/* Per-round breakdown */}
                <h3 className="si-detail-section-heading">Round breakdown</h3>
                {!standing || standing.roundScores.length === 0 ? (
                  <p className="si-detail-empty">No scored rounds yet.</p>
                ) : (
                  <table className="si-detail-rounds">
                    <thead>
                      <tr>
                        <th>Round</th>
                        <th>Questions</th>
                        <th>Bonus</th>
                        <th>Subtotal</th>
                        <th>Doubled</th>
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {standing.roundScores.map((r) => (
                        <tr key={r.roundIndex}>
                          <td>R{r.roundIndex}</td>
                          <td>{r.questionPoints}</td>
                          <td>{r.bonusPoints}</td>
                          <td>{r.subtotal}</td>
                          <td>{r.isDoubled ? '2×' : '—'}</td>
                          <td className="si-detail-round-total">{r.total}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Standings Modal ── */}
      {showStandings && (
        <div className="si-overlay" onClick={() => setShowStandings(false)}>
          <div className="si-modal" onClick={(e) => e.stopPropagation()}>
            <div className="si-modal-header">
              <Heading as="h2" size="3">Current Standings</Heading>
              <IconButton
                size="1"
                variant="ghost"
                color="gray"
                aria-label="Close"
                onClick={() => setShowStandings(false)}
              >
                &times;
              </IconButton>
            </div>
            <div className="si-modal-body">
              <table className="si-standings-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Team</th>
                    <th>Double</th>
                    <th>Score</th>
                  </tr>
                </thead>
                <tbody>
                  {standings.map((s) => {
                    const team = teams.find((t) => t.id === s.teamId);
                    return (
                    <tr key={s.teamId}>
                      <td
                        className={`si-standings-rank ${
                          s.rank === 1
                            ? 'si-rank-1'
                            : s.rank === 2
                              ? 'si-rank-2'
                              : s.rank === 3
                                ? 'si-rank-3'
                                : ''
                        }`}
                      >
                        {s.rank}
                      </td>
                      <td>{s.teamName}</td>
                      <td>
                        {team?.doubledRound ? (
                          <span className="si-double-badge used">R{team.doubledRound}</span>
                        ) : (
                          <span className="si-double-badge unused">{'\u2014'}</span>
                        )}
                      </td>
                      <td>{s.grandTotal}</td>
                    </tr>
                    );
                  })}
                  {standings.length === 0 && (
                    <tr>
                      <td colSpan={4} style={{ textAlign: 'center', color: 'var(--gray-9)' }}>
                        No teams yet
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── Phone check-ins ── */}
      {showCheckins && (
        <div className="si-overlay" onClick={() => setShowCheckins(false)}>
          <div className="si-modal" onClick={(e) => e.stopPropagation()}>
            <div className="si-modal-header">
              <Heading as="h2" size="3">Phone check-ins</Heading>
              <IconButton
                size="1"
                variant="ghost"
                color="gray"
                aria-label="Close"
                onClick={() => setShowCheckins(false)}
              >
                &times;
              </IconButton>
            </div>
            <div className="si-modal-body">
              {event.checkInEnabled && <CheckInQr pin={event.pin} size={120} />}
              <div style={{ marginTop: 12 }}>
                <CheckInPanel teams={teams} onChanged={reloadTeams} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Add Team Modal ── */}
      {showAddTeam && (
        <div className="si-overlay" onClick={() => setShowAddTeam(false)}>
          <div className="si-modal" onClick={(e) => e.stopPropagation()}>
            <div className="si-modal-header">
              <Heading as="h2" size="3">Add Team</Heading>
              <IconButton
                size="1"
                variant="ghost"
                color="gray"
                aria-label="Close"
                onClick={() => setShowAddTeam(false)}
              >
                &times;
              </IconButton>
            </div>
            <div className="si-modal-body">
              <form className="si-add-form" onSubmit={handleAddTeam}>
                <div className="si-add-field">
                  <label>Team Name</label>
                  <TextField.Root
                    size="2"
                    value={newTeamName}
                    onChange={(e) => setNewTeamName(e.target.value)}
                    placeholder="Enter team name"
                    autoFocus
                    required
                  />
                </div>
                <div className="si-add-field">
                  <label>Player Count (optional)</label>
                  <TextField.Root
                    size="2"
                    type="number"
                    value={newTeamCount}
                    onChange={(e) => setNewTeamCount(e.target.value)}
                    placeholder="Number of players"
                    min={1}
                  />
                </div>
                <div className="si-add-actions">
                  <Button
                    type="button"
                    variant="soft"
                    color="gray"
                    onClick={() => setShowAddTeam(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={!newTeamName.trim() || addingTeam}
                  >
                    {addingTeam ? 'Adding...' : 'Add Team'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
    </Theme>
  );
}

// Click-to-edit team name cell. Renders as plain text until clicked,
// then becomes a focused input. Enter or blur commits, Esc reverts.
// Kept as a small subcomponent so we don't drag `editingTeamId` state
// into the (already large) ScoringInterface body.
function TeamNameCell({
  name,
  onCommit,
}: {
  name: string;
  onCommit: (next: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(name);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!editing) setDraft(name);
  }, [name, editing]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  if (!editing) {
    return (
      <button
        type="button"
        className="si-team-name-button"
        onClick={() => setEditing(true)}
        title="Click to rename"
      >
        {name}
      </button>
    );
  }

  return (
    <input
      ref={inputRef}
      className="si-team-name-input"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        onCommit(draft);
        setEditing(false);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          onCommit(draft);
          setEditing(false);
        } else if (e.key === 'Escape') {
          setDraft(name);
          setEditing(false);
        }
      }}
      maxLength={80}
    />
  );
}

// ────────────────────────────────────────────
// Sheets view (design card 2d) — one team's paper sheet at a time.
// Built for grading in whatever order sheets land on the table: jump to
// any team by typing two letters, and each sheet writes to the round its
// chip selects, so a straggler R2 sheet can be graded mid-R4 without
// touching that team's other rounds.
// ────────────────────────────────────────────

export interface SheetsViewProps {
  teams: EventTeam[];
  rounds: Round[];
  scores: ScoreRecord[];
  standings: TeamStanding[];
  getQuestionScore: (teamId: string, roundIndex: number, questionId: string) => ScoreState;
  setQuestionScore: (teamId: string, roundIndex: number, questionId: string, next: ScoreState) => void;
  getBonusPoints: (teamId: string, roundIndex: number) => number;
  setBonusValue: (teamId: string, roundIndex: number, value: number) => void;
  toggleDouble: (teamId: string, roundIndex: number) => void;
  pointsConfig: Record<number, number>;
  /** Round (1-based) to open first. Defaults to the first round. */
  initialRound?: number;
  /** Called when the host picks another round, so Grid view opens the same round. */
  onRoundChange?: (roundIndex: number) => void;
}

// Exported for tests: the visual-round guards below are the fix for Sheets
// writing per-question rows that standings then discard, and they're only
// meaningfully checked by driving this view directly.
export function SheetsView({
  teams,
  rounds,
  scores,
  standings,
  getQuestionScore,
  setQuestionScore,
  getBonusPoints,
  setBonusValue,
  toggleDouble,
  pointsConfig,
  initialRound,
  onRoundChange,
}: SheetsViewProps) {
  const activeTeams = useMemo(() => teams.filter((t) => !t.leftEarly), [teams]);
  const [activeTeamId, setActiveTeamId] = useState<string>(() => activeTeams[0]?.id ?? '');
  const [activeSheetRound, setActiveSheetRoundState] = useState<number>(
    initialRound ?? (rounds[0]?.orderIndex != null ? rounds[0].orderIndex + 1 : 1),
  );
  const setActiveSheetRound = (roundIndex: number) => {
    setActiveSheetRoundState(roundIndex);
    onRoundChange?.(roundIndex);
  };
  const [jump, setJump] = useState('');
  const jumpRef = useRef<HTMLInputElement | null>(null);

  // Keep a valid active team as teams load/change.
  useEffect(() => {
    if (!activeTeams.some((t) => t.id === activeTeamId) && activeTeams[0]) {
      setActiveTeamId(activeTeams[0].id);
    }
  }, [activeTeams, activeTeamId]);

  // Has this team been scored for this round? Any QUESTION or VISUAL record
  // for the round counts — grading one round never implies another.
  const roundScored = useCallback(
    (teamId: string, roundIndex: number): boolean => isRoundScored(scores, teamId, roundIndex),
    [scores],
  );

  const roundByIndex = useCallback(
    (roundIndex: number): Round | undefined => rounds.find((r) => r.orderIndex + 1 === roundIndex),
    [rounds],
  );

  const activeTeam = activeTeams.find((t) => t.id === activeTeamId) ?? activeTeams[0];
  const sheetRound = roundByIndex(activeSheetRound);
  const ppq = pointsConfig[activeSheetRound] ?? 2;
  // Visual rounds are scored as one number per team in Grid view. This view
  // must not write per-question rows for them: standings short-circuit a
  // round to its VISUAL row (utils/standings.ts) and silently discard any
  // QUESTION/BONUS rows alongside it, so anything typed here would vanish.
  const isVisual = sheetRound?.roundType === 'VISUAL';

  // Teams matching the jump box, best first — prefix wins, then substring,
  // then typo-tolerant (see teamMatch.ts).
  const jumpMatches = useMemo(() => matchTeams(activeTeams, jump), [jump, activeTeams]);

  // Per-round status chip for a team in the rail: ✓ scored, current-target
  // amber, or red "!" when an earlier round is still missing.
  const chipState = useCallback(
    (teamId: string, roundIndex: number): 'done' | 'target' | 'missing' | 'todo' => {
      if (roundScored(teamId, roundIndex)) return 'done';
      if (roundIndex === activeSheetRound) return 'target';
      if (roundIndex < activeSheetRound) return 'missing';
      return 'todo';
    },
    [roundScored, activeSheetRound],
  );

  // Sheet question points + max for the header ("8 / 16").
  const sheetTotals = useMemo(() => {
    if (!activeTeam || !sheetRound) return { earned: 0, max: 0 };
    // A visual round has no per-question rows to add up — read the score the
    // grid actually wrote, out of one point per image.
    if (isVisual) {
      const rs = standings
        .find((s) => s.teamId === activeTeam.id)
        ?.roundScores.find((r) => r.roundIndex === activeSheetRound);
      // total is post-doubling, so the ceiling has to double with it or a
      // doubled visual round reads as an impossible 16 / 8.
      const max = sheetRound.questions.length * (rs?.isDoubled ? 2 : 1);
      return { earned: rs?.total ?? 0, max };
    }
    // Each question counts its own value (or the round value). Half credit
    // is half of that, rounded down, the same as the standings.
    let earned = 0;
    for (const q of sheetRound.questions) {
      const st = getQuestionScore(activeTeam.id, activeSheetRound, q.id);
      earned += markPoints(st, q.points ?? ppq);
    }
    return { earned, max: roundQuestionMax(sheetRound, pointsConfig) };
  }, [activeTeam, sheetRound, activeSheetRound, ppq, pointsConfig, getQuestionScore, isVisual, standings]);

  const bonus = activeTeam ? getBonusPoints(activeTeam.id, activeSheetRound) : 0;
  const isDoubled = activeTeam?.doubledRound === activeSheetRound;

  // ✓ tap: not-full → full, full → half (matches the grid's cycle).
  const tapCorrect = (questionId: string) => {
    if (!activeTeam) return;
    const cur = getQuestionScore(activeTeam.id, activeSheetRound, questionId);
    setQuestionScore(activeTeam.id, activeSheetRound, questionId, cur === 'full' ? 'half' : 'full');
  };
  // ✗ tap: wrong → clear, else → wrong.
  const tapWrong = (questionId: string) => {
    if (!activeTeam) return;
    const cur = getQuestionScore(activeTeam.id, activeSheetRound, questionId);
    setQuestionScore(activeTeam.id, activeSheetRound, questionId, cur === 'wrong' ? null : 'wrong');
  };

  // First team after the current one that still lacks a score for the
  // active round; wraps around. Returns null when every team is scored.
  const nextUnscored = useCallback((): EventTeam | null => {
    const n = activeTeams.length;
    if (n === 0) return null;
    const start = Math.max(0, activeTeams.findIndex((t) => t.id === activeTeamId));
    for (let i = 1; i <= n; i++) {
      const t = activeTeams[(start + i) % n]!;
      if (!roundScored(t.id, activeSheetRound)) return t;
    }
    return null;
  }, [activeTeams, activeTeamId, activeSheetRound, roundScored]);

  const saveNext = () => {
    const next = nextUnscored();
    if (next) setActiveTeamId(next.id);
  };

  const selectTeam = (teamId: string) => {
    setActiveTeamId(teamId);
    setJump('');
  };

  // Keyboard: ⌘K focus jump, 1–8 cycle that question, Enter save→next.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        jumpRef.current?.focus();
        return;
      }
      const inField = e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement;
      if (inField) {
        if (e.key === 'Enter' && jumpMatches.length > 0) {
          e.preventDefault();
          selectTeam(jumpMatches[0]!.team.id);
        }
        return;
      }
      if (e.key === 'Enter') { e.preventDefault(); saveNext(); return; }
      if (/^[1-8]$/.test(e.key) && sheetRound && !isVisual) {
        const q = sheetRound.questions[parseInt(e.key, 10) - 1];
        if (q) { e.preventDefault(); tapCorrect(q.id); }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  const standingsMap = useMemo(() => new Map(standings.map((s) => [s.teamId, s])), [standings]);
  const chipRounds = rounds.map((r) => r.orderIndex + 1);
  // Sheets scored for this round only. Other rounds do not count.
  const progress = roundProgress(teams, scores, activeSheetRound);

  if (!activeTeam) return null;

  return (
    <div className="si-sheets">
      {/* Left rail — jump + status */}
      <div className="si-sheets-rail">
        <div className="si-jump">
          <span className="si-jump-icon">⌕</span>
          <input
            ref={jumpRef}
            className="si-jump-input"
            value={jump}
            onChange={(e) => setJump(e.target.value)}
            placeholder="Type 2 letters — jump to team (⌘K)"
            aria-label="Jump to team"
          />
        </div>

        {jump.trim() ? (
          <div className="si-jump-matches">
            {jumpMatches.length === 0 ? (
              <div className="si-jump-empty">No team matches “{jump.trim()}”.</div>
            ) : (
              jumpMatches.map(({ team: t, span }, i) => {
                return (
                  <button
                    key={t.id}
                    type="button"
                    className={`si-jump-match ${i === 0 ? 'first' : ''}`}
                    onClick={() => selectTeam(t.id)}
                  >
                    <span className="si-jump-name">
                      {/* Bold the literal hit; a typo-only match has nothing to bold. */}
                      {span ? (
                        <>
                          {t.name.slice(0, span[0])}
                          <b>{t.name.slice(span[0], span[1])}</b>
                          {t.name.slice(span[1])}
                        </>
                      ) : (
                        t.name
                      )}
                    </span>
                    <span className="si-chip-row">
                      {chipRounds.map((ri) => (
                        <span key={ri} className={`si-chip si-chip-${chipState(t.id, ri)}`}>
                          R{ri}{chipState(t.id, ri) === 'done' ? '✓' : chipState(t.id, ri) === 'missing' ? '!' : ''}
                        </span>
                      ))}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        ) : (
          <>
            <span className="si-rail-overline">
              ALL TEAMS · R{activeSheetRound} STATUS · {progress.scored}/{progress.total} SCORED
            </span>
            <div className="si-rail-list">
              {activeTeams.map((t) => {
                const scored = roundScored(t.id, activeSheetRound);
                const st = standingsMap.get(t.id);
                const rs = st?.roundScores.find((r) => r.roundIndex === activeSheetRound);
                const earned = rs?.total;
                const rr = roundByIndex(activeSheetRound);
                // Visual rounds score one point per image. Other rounds add
                // the question values. `earned` is post-doubling, so the
                // ceiling doubles too.
                const max = (rr ? roundQuestionMax(rr, pointsConfig) : 0)
                  * (rs?.isDoubled ? 2 : 1);
                return (
                  <button
                    key={t.id}
                    type="button"
                    className={`si-rail-team ${scored ? 'scored' : 'unscored'} ${t.id === activeTeamId ? 'active' : ''}`}
                    onClick={() => selectTeam(t.id)}
                  >
                    <span className="si-rail-mark">{scored ? '✓' : '◯'}</span>
                    <span className="si-rail-name">{t.name}</span>
                    {scored && earned != null && (
                      <span className="si-rail-score">{earned}/{max}</span>
                    )}
                  </button>
                );
              })}
            </div>
          </>
        )}

        <div className="si-rail-note">
          Grading an old-round sheet never touches other rounds — each sheet
          writes only to the round its chip selects.
        </div>
      </div>

      {/* Sheet panel */}
      <div className="si-sheet">
        <div className="si-sheet-head">
          <span className="si-sheet-team">{activeTeam.name}</span>
          <span className="si-sheet-chips">
            {chipRounds.map((ri) => {
              const scored = roundScored(activeTeam.id, ri);
              const isThis = ri === activeSheetRound;
              return (
                <button
                  key={ri}
                  type="button"
                  className={`si-sheet-chip ${isThis ? 'this' : scored ? 'done' : 'todo'}`}
                  onClick={() => setActiveSheetRound(ri)}
                  title={isThis ? 'This sheet writes to this round' : `Switch this grading pass to round ${ri}`}
                >
                  {isThis ? `R${ri} — this sheet` : `R${ri}${scored ? ' ✓' : ''}`}
                </button>
              );
            })}
          </span>
          <span className="si-sheet-spacer" />
          <span className="si-sheet-total">
            Sheet total <b>{sheetTotals.earned}</b> / {sheetTotals.max}
          </span>
          {/* The whole-night total stays visible, but small and muted. */}
          <span className="si-sheet-night" title="Whole-night total for this team">
            Night {standingsMap.get(activeTeam.id)?.grandTotal ?? 0}
          </span>
        </div>

        {!sheetRound ? (
          <div className="si-sheet-empty">This round isn’t in the quiz.</div>
        ) : sheetRound.roundType === 'VISUAL' ? (
          <div className="si-sheet-empty">
            Round {activeSheetRound} is a visual round — score it in Grid view.
          </div>
        ) : (
          <div className="si-sheet-rows">
            {sheetRound.questions.map((q, i) => {
              const st = getQuestionScore(activeTeam.id, activeSheetRound, q.id);
              return (
                <div key={q.id} className={`si-sheet-row ${i === 0 ? 'current' : ''}`}>
                  <span className="si-sheet-qnum">
                    Q{q.orderIndex + 1}
                    {q.points != null && q.points !== ppq && (
                      <span className="si-sheet-pts">{q.points}pt</span>
                    )}
                  </span>
                  <span className="si-sheet-answer" title={q.answer}>{q.answer || '—'}</span>
                  <button
                    type="button"
                    className={`si-sheet-mark correct ${st === 'full' ? 'on' : st === 'half' ? 'half' : ''}`}
                    onClick={() => tapCorrect(q.id)}
                    title="Correct — tap again for half credit"
                    aria-label={`Mark question ${q.orderIndex + 1} correct`}
                  >
                    {st === 'half' ? '½' : '✓'}
                  </button>
                  <button
                    type="button"
                    className={`si-sheet-mark wrong ${st === 'wrong' ? 'on' : ''}`}
                    onClick={() => tapWrong(q.id)}
                    title="Wrong"
                    aria-label={`Mark question ${q.orderIndex + 1} wrong`}
                  >
                    ✗
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <div className="si-sheet-extras">
          {/* No bonus on visual rounds — Grid view doesn't offer one, and a
              BONUS row written here would be discarded by standings. */}
          {!isVisual && (
            <>
              <span className="si-sheet-extra-label">BONUS</span>
              <span className="si-stepper">
                <button type="button" onClick={() => setBonusValue(activeTeam.id, activeSheetRound, Math.max(0, bonus - 1))}>−</button>
                <span className="si-stepper-val">{bonus}</span>
                <button type="button" onClick={() => setBonusValue(activeTeam.id, activeSheetRound, bonus + 1)}>+</button>
              </span>
            </>
          )}
          <span className="si-sheet-extra-label">DOUBLE</span>
          <button
            type="button"
            className={`si-mini-toggle ${isDoubled ? 'on' : ''}`}
            onClick={() => toggleDouble(activeTeam.id, activeSheetRound)}
            title={isDoubled ? 'Double is on this round' : 'Mark this as the team’s double round'}
            aria-pressed={isDoubled}
          >
            <span className="si-mini-toggle-knob" />
          </button>
          <span className="si-sheet-spacer" />
          <span className="si-sheet-hints">
            {isVisual ? '⌘K jump · ⏎ next' : '1–8 toggles · B bonus · ⌘K jump · ⏎ next'}
          </span>
        </div>

        <div className="si-sheet-actions">
          <button type="button" className="si-sheet-btn ghost" onClick={() => { /* writes are already saved */ }}>
            Save, stay on team
          </button>
          <button type="button" className="si-sheet-btn primary" onClick={saveNext}>
            Save → next unscored R{activeSheetRound} sheet
          </button>
        </div>
      </div>
    </div>
  );
}
